import Button from '@components/Button';
import imageUrlBuilder from '@helpers/imageUrlFor';
import {
  getAvailableImageUrl,
  getDefaultMediaSrc,
  getMarkingSuffix,
  isVideoType,
  productDownloads,
  productImageSrc,
} from '@helpers/product';
import { useLocation } from '@reach/router';
import FluidImage from '@utility/FluidImage';
import Link from '@utility/Link';
import useIsomorphicLayoutEffect from '@utility/useIsomorphicLayoutEffect';
import cn from 'classnames';
import PropTypes from 'prop-types';
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from 'react';
import DownloadIcon from '../../../static/images/icons/icon-download-arrow-m.svg';
import * as styles from './styles';
import AuthUserContext from '../../context/AuthUserContext';
import { useReactToPrint } from 'react-to-print';
import CardVideoPlayer from '../CardVideoPlayer';
import ContentWithSuperscript from '../CollectionTitle';
import useProductMarkingHook from '@src/utility/useProductMarkingHook';

const ProductCard = ({
  data: {
    title,
    partial_sku,
    slug,
    slugPrefix,
    price,
    hasHoverImage,
    hoverImageType,
    defaultMediaType,
    defaultFinish,
    discountPrice,
    downloads,
    isNew,
    image,
    code,
    category,
    sampleDownload,
    _type,
    showOnProductCard,
    marking
  },
  finishCode,
  showPrice,
  isFinish,
  isDrawing,
}) => {
  const [hasVisibleMenu, setVisibleMenu] = useState(false);
  const [productAdded, setProductAdded] = useState(false);
  const [pageContent, setPageContent] = useState('');
  const [cardImgSrc, setCardImgSrc] = useState('');
  const [baseImgUrl, setBaseImgUrl] = useState('');
  const [hoverImg, setHoverImgUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(true);
  const [finishAdded, setFinishAdded] = useState(false);
  const [finalSlug, setFinalSlug] = useState(slug?.current);
  const [, setCanHover] = useState(true);
  const location = useLocation();
  const { authUser } = useContext(AuthUserContext);
  const markingData = useProductMarkingHook(marking?.length > 0 ? marking.map(e => e._id ?? e._ref) : []);

  useEffect(() => {
    if (finishCode) {
      generateFinalSlug();
    }
    
  }, [])

  useIsomorphicLayoutEffect(() => {
    setCanHover(!window.matchMedia('(hover: none)').matches);
  }, []);

  const clickOutside = (e) => {
    if (hasVisibleMenu && !e.target.closest('.js-downloads')) {
      setVisibleMenu(false);
    }
  };

  useEffect(() => {
    let baseSrc = productImageSrc(
      slugPrefix ? slugPrefix.current : title,
      'plp',
      partial_sku,
      renderImageAttributes(),
      762,
      'jpg',
      showOnProductCard && location.pathname !== '/resources' ? markingData : null
    );
    let imgsrc = getDefaultMediaSrc(
      slugPrefix ? slugPrefix.current : title, // title as fallback so insufficient data does not break the product card
      'plp',
      partial_sku,
      renderImageAttributes(),
      762,
      defaultMediaType?.toLowerCase(),
      showOnProductCard && location.pathname !== '/resources' ? markingData : null
    );

    setCardImgSrc(imgsrc);
    setBaseImgUrl(baseSrc); //DOC: Used to pass as light image for videos default
    setImageLoading(false);
    hasHoverImage && getHoverImage(baseSrc);
  }, []);

  useEffect(() => {
    if (document !== 'undefined') {
      document.addEventListener('click', clickOutside);
      return () => {
        return document.removeEventListener('click', clickOutside);
      };
    }
  });

  const downloadsMenuHandler = (event) => {
    event.currentTarget.blur();
    setVisibleMenu(!hasVisibleMenu);
  };

  const getHoverImage = (baseImg) => {
    if (!hasHoverImage) return;
    let hoverUrl = getAvailableImageUrl(baseImg, hoverImageType);

    if (!hoverImageType || hoverImageType === 'gif') {
      hoverUrl = hoverUrl.split('?')[0]; //to remove w param, which restricts gif
    }

    setHoverImgUrl(hoverUrl);

    //make an empty request to this image so that it loads.
    preloadHoverImg(hoverUrl);
  };

  const generateFinalSlug = async () => {
    const slugUrl = slug.current.replace(defaultFinish?.code?.toLowerCase(), finishCode?.toLowerCase());
    const urlExists = await checkIfUrlExists(slugUrl);
    if (urlExists) {
      setFinalSlug(slugUrl);
    }
  }

  const checkIfUrlExists = async (url) => {
    try {
      const response = await fetch(location.origin + '/' + url);
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  const preloadHoverImg = async (hoverUrl) => {
    const img = new Image();
    img.src = hoverUrl;
    img.onload = () => {};
  };

  const onFinishAdded = () => {
    setFinishAdded(true);
    setTimeout(() => {
      setFinishAdded(false);
    }, 2000);
  };

  const renderImageAttributes = () => {
    if (isDrawing) {
      return ['draw'];
    }
    if (finishCode && finishCode !== '') {
      return [finishCode];
    }
    return [];
  };

  const renderImage = () => {
    if (
      _type === 'product' &&
      !imageLoading &&
      !isVideoType(defaultMediaType)
    ) {
      const showHoverImg = hoverImg && !isDrawing;
      return (
        <div className={showHoverImg ? styles.productCardUpperImagesContainer : ''}>
          <img
            className={`${styles.img1} object-cover h-full w-full`}
            src={cardImgSrc}
            alt={title}
            height="auto"
            width="100%"
          />

          {showHoverImg && (
            <img
              className={`${styles.img2} object-cover h-full w-full`}
              src={hoverImg}
              alt={title}
              height="auto"
              width="100%"
            />
          )}
        </div>
      );
    }

    //DOC: Show default jpg image, when a finish code is present, event if the defaultMediaType is a video
    if (_type === 'product' && !imageLoading && isVideoType(defaultMediaType) && !finishCode) {
      return <CardVideoPlayer url={cardImgSrc} lightImage={baseImgUrl} />;
    }

    if (_type === 'product' && !imageLoading && isVideoType(defaultMediaType) && finishCode) {
      return <div>
        <img
            className={`${styles.img1} object-cover h-full w-full`}
            src={baseImgUrl}
            alt={title}
            height="auto"
            width="100%"
          />
      </div>
    }

    if (_type === 'product' && imageLoading) {
      return <div className={styles.loadingBg}></div>;
    }

    if (!price) {
      return (
        <div
          className="z-10 relative cursor-pointer h-full"
          onClick={() => window.open(sampleDownload)}
        >
          <FluidImage
            className="sm-down"
            src={image}
            maxWidth={568}
            maxHeight={852}
          />
          <Button
            aria-label={`Download spec ${category?.title} ${code} digital sample`}
            className={`${styles.productCardLink} ${styles.productCardLinkFinish} ${styles.productCardLinkAddCart}`}
          >
            <span>
              Download<br></br> Spec
            </span>
          </Button>
        </div>
      );
    }

    if (_type === 'finish') { //round finish cta
      return (
        <div
          onClick={onFinishAdded}
          className="z-10 relative cursor-pointer h-full snipcart-add-item"
          data-item-id={`${code}`}
          data-item-name={title}
          data-item-price={price / 100}
          data-item-url={
            process.env.GATSBY_DEVELOPMENT_MODE === 'true'
              ? `https://rbw-qa.netlify.app${location.pathname}`
              : location.pathname
          }
          data-item-description={code}
          data-item-categories={'Finish Sample'}
          data-item-image={imageUrlBuilder(image)}
          data-item-custom100-name="Actual Price"
          data-item-custom100-value={price / 100}
          data-item-custom100-type="hidden"
          data-item-custom101-name="isUserTrade"
          data-item-custom101-value={!!authUser?.user_trade}
          data-item-custom101-type="hidden"
          data-item-custom1-name="Finish Category"
          data-item-custom2-name={category?.title}
          data-context="Add to Cart"
          data-context-interacted="Added to Cart"
          data-item-stackable="always"
        >
          <FluidImage
            className="sm-down"
            src={image}
            maxWidth={568}
            maxHeight={852}
          />
          <Button
            aria-label={`Add to cart ${category?.title} ${code} sample`}
            to="#"
            className={cn(
              `${styles.productCardLink} ${styles.productCardLinkFinish} ${styles.productCardLinkAddCart} btn-interaction btn-interaction--black focus:outline-none sm-down:pointer-events-none hidden sm:block`,
              { 'is-loading': finishAdded },
            )}
          >
            <span>
              Add to <br></br>cart
            </span>
            <span>
              Added to <br></br>cart
            </span>
          </Button>
        </div>
      );
    }

    return (
      <div
        onClick={onFinishAdded}
        className="z-10 relative cursor-pointer h-full"
      >
        <FluidImage
          className="sm-down"
          src={image}
          maxWidth={568}
          maxHeight={852}
        />
      </div>
    );
  };

  const renderDownloadsMenu = () => {
    return (
      <ul
        className={cn(styles.productCardDownloadsMenu, 'js-downloads', {
          [styles.isVisible]: hasVisibleMenu,
        })}
      >
        {productDownloads(downloads).map((download) => (
          <li className={styles.productCardDownloadsItem} key={download.title}>
            <Link
              to={download.file}
              className="block w-full py-2 sm:py-4 pl-6"
              styled={false}
              download
              aria-label={download.title}
            >
              {download.title}
            </Link>
          </li>
        ))}
        <li className={styles.productCardDownloadsItem} key="blank_sheet">
          <Link
            className="block w-full py-2 sm:py-4 pl-6"
            styled={false}
            onClick={(e) => generatePrint(e)}
            aria-label="Download Blank tear sheet"
          >
            Blank tear sheet
          </Link>
        </li>
      </ul>
    );
  };

  const renderFinishLink = () => {
    return (
      <div>
        {title}
        {code ? (
          <span
            className={`subpixel-antialiased ${styles.productCardLinkCode}`}
          >
            {code}
          </span>
        ) : null}
      </div>
    );
  };

  //* shown only on mobile
  const renderFinishCta = () => {
    if (!price) {
      return (
        <Link
          to={sampleDownload}
          className={`${styles.productCardCta} btn btn-default btn-black focus:outline-none block sm:hidden`}
          styled={false}
          download
          aria-label={`Download spec ${category?.title} ${code} digital sample`}
        >
          Download
        </Link>
      );
    }

    //rectangle finishes button only on mobile
    return (
      <Button
        aria-label={`Add to cart ${category.title} ${code} sample`}
        to="#"
        className={cn(
          `${styles.productCardCta} block sm:hidden btn btn-default btn-black btn-interaction snipcart-add-item focus:outline-none`,
          {
            'is-loading': productAdded,
          },
        )}
        data-item-id={`${code}`}
        data-item-name={title}
        data-item-price={price / 100}
        data-item-url={
          process.env.GATSBY_DEVELOPMENT_MODE === 'true'
            ? `https://rbw-qa.netlify.app${location.pathname}`
            : location.pathname
        }
        data-item-description={code}
        data-item-categories={'Finish Sample'}
        data-item-image={imageUrlBuilder(image)}
        data-item-custom100-name="Actual Price"
        data-item-custom100-value={price / 100}
        data-item-custom100-type="hidden"
        data-item-custom101-name="isUserTrade"
        data-item-custom101-value={!!authUser?.user_trade}
        data-item-custom101-type="hidden"
        data-item-custom1-name="Finish Category"
        data-item-custom2-name={category.title}
        data-item-stackable="always"
        onClick={() => {
          setProductAdded(true);
          setTimeout(() => {
            setProductAdded(false);
          }, 1500);
        }}
      >
        <span>Add to Cart</span>
        <span>Added to Cart</span>
      </Button>
    );
  };

  const renderProductAction = () => {
    if (slug && slug.current) {
      if (isDrawing) {
        return (
          <Button
            className={styles.productCardLink}
            data-context="Download Files"
            onClick={downloadsMenuHandler}
            aria-label={title}
          >
            <ContentWithSuperscript content={title} />
          </Button>
        );
      }

      return (
        <Link
          to={`/${finalSlug}`}
          className={styles.productCardLink}
          styled={false}
          data-context="Build"
          aria-label={`Build ${title}`}
        >
          <ContentWithSuperscript content={title} />
        </Link>
      );
    }
    return title;
  };

  const renderAction = () => {
    return _type === 'finish' ? renderFinishLink() : renderProductAction();
  };

  const generatePrintDialog = useReactToPrint({
    content: () => contentsRef.current,
    print: (node) =>
      new Promise((res, rej) => {
        node.contentWindow.print();
        res(1);
      }),
  });

  const generatePrint = (event) => {
    event.currentTarget.blur();
    setVisibleMenu(!hasVisibleMenu);
    let productUrl = `${location.origin}/${slug.current}`;
    const prodSlug =
      process.env.GATSBY_DEVELOPMENT_MODE === 'true'
        ? `http://localhost:8000/${slug.current}`
        : productUrl;

    fetch(prodSlug)
      .then((response) => response.text())
      .then((html) => {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        doc.getElementById('product_code_blank').style.display = 'block';
        doc.getElementById('product_code').style.display = 'none';
        doc.getElementById('__productControls').classList.add(styles.controls);
        let selectedElements = doc.getElementsByClassName(
          'btn-config--selected',
        );
        Array.from(selectedElements).forEach((e) => {
          e.style.backgroundColor = 'transparent';
        });

        if (window?.location.pathname === '/resources') {
          doc.getElementById('printDrawings').classList.add('!mt-0');
        }

        doc.body.classList.add('is-blank-product');
        const data = doc.body.innerHTML;
        setPageContent(data);

        generatePrintDialog();
      });
  };

  const renderPrice = () => {
    if (!price && isFinish) {
      return <p className="type-sans-130 text-mono-500">Digital Sample</p>;
    }

    if (!price) return;

    return (
      <p className="type-sans-130 text-mono-500">
        {`${!isFinish ? 'From' : ''} $${
          (authUser?.user_trade && !isFinish ? discountPrice || price : price) /
          100
        }`}
      </p>
    );
  };

  let contentsRef = useRef(null);

  return (
    <div className={styles.productCard}>
      <div
        className={cn(styles.productCardUpper, {
          [styles.isBlurred]: hasVisibleMenu,
          [styles.productCardUpperSmall]: isFinish,
        })}
      >
        <div className={cn(styles.productCardImageWrapper)}>
          {renderImage()}
        </div>
        {!isFinish && downloads && renderDownloadsMenu()}
      </div>
      <div>
        <div className="flex justify-between items-start">
          <h3
            className={cn('type-sans-130', styles.willHide, {
              [styles.isHidden]: hasVisibleMenu,
            })}
          >
            {renderAction()}
          </h3>
          {!isFinish && downloads && (
            <button
              className={cn(styles.productCardDownloadsButton, 'js-downloads', {
                [styles.isActive]: hasVisibleMenu,
              })}
              onClick={downloadsMenuHandler}
              type="button"
              aria-label="Downloads Menu"
            >
              <img
                className={styles.productCardDownloadsIcon}
                src={DownloadIcon}
                alt="Download icon"
              />
              {hasVisibleMenu ? (
                'Close'
              ) : (
                <span className="hidden sm:inline">Downloads</span>
              )}
            </button>
          )}
        </div>
        <div
          className={cn(styles.willHide, { [styles.isHidden]: hasVisibleMenu })}
        >
          {showPrice && renderPrice()}
          {isNew && <p className="type-sans-140">New</p>}
        </div>
        {isFinish && renderFinishCta()}
      </div>

      <div style={{ display: 'none' }}>
        <ProductPrint pageContent={pageContent} ref={contentsRef} />
      </div>
    </div>
  );
};

ProductCard.defaultProps = {
  data: {},
  finishCode: null,
  showPrice: true,
  isFinish: false,
  isDrawing: false,
  _type: '',
};

export default ProductCard;

const ProductPrint = forwardRef((props, ref) => {
  return (
    <div ref={ref}>
      <div dangerouslySetInnerHTML={{ __html: props.pageContent }} />
    </div>
  );
});
